import {Descendant, Text, Element} from "slate";
import {CustomElement, CustomText} from "./custom-types";
import escapeHtml from 'escape-html'

export const deserialize = (html: string): Descendant[] => {
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    return deserializeDOMElement(parsed.body)
}

const deserializeDOMElement = (el: HTMLElement | ChildNode): Descendant[] => {
    if (el.nodeType === Node.TEXT_NODE) {
        return [{'text': el.textContent ?? ""}]
    } else if (el.nodeType !== Node.ELEMENT_NODE || el.nodeName.toLowerCase() === 'br') {
        return []
    }

    let children = Array.from(el.childNodes).map(deserializeDOMElement).flat()
    if (children.length === 0) {
        return []
    }
    if (isHTMLElement(el) && el.style.fontStyle == 'italic') {
        children = applyToAllTextNodes(children, child => child.italic === undefined ? ({...child, italic: true}) : child)
    }
    if (isHTMLElement(el) && (el.style.fontWeight == '400' || el.style.fontWeight == 'normal')) {
        children = applyToAllTextNodes(children, child => child.bold === undefined ? ({...child, bold: false}) : child)
    }
    if (isHTMLElement(el) && (parseInt(el.style.fontWeight) > 400  || el.style.fontWeight == 'bold')) {
        children = applyToAllTextNodes(children, child => child.bold === undefined ? ({...child, bold: true}) : child)
    }
    const nodeName=  el.nodeName.toLowerCase()
    switch (nodeName) {
        case 'h1':
        case 'h2':
        case 'h3':
            return [{type: nodeName, children}]
        case 'p':
            return [{type: 'paragraph', children}]
        case 'em':
        case 'i':
            return applyToAllTextNodes(children, child => child.italic === undefined ? ({...child, italic: true}) : child)
        case 'strong':
        case 'b':
            return applyToAllTextNodes(children, child => child.bold === undefined ? ({...child, bold: true}) : child)
        case 'u':
            return applyToAllTextNodes(children, child => child.underline === undefined ? ({...child, underline: true}) : child)
    }

    return children
}

const isHTMLElement = (el: ChildNode): el is HTMLElement =>
    el.nodeType === Node.ELEMENT_NODE && (el as HTMLElement).style !== undefined

const applyToAllTextNodes = (nodes: Descendant[], fn: (node: Text) => Text): Descendant[] =>
    nodes.map(node => {
        if (Text.isText(node)) {
            return fn(node)
        } else if (Element.isElement(node)) {
            return {
                ...node,
                children: applyToAllTextNodes(node.children, fn)
            }
        }
        return node
    })

export const serialize = (nodes: Descendant[]): string =>
    nodes.map(serializeNode).join("")

const serializeNode = (node: Descendant): string => {
    switch (true) {
        case Text.isText(node):
            return serializeText(node)
        case Element.isElement(node):
            return serializeElement(node)
        default:
            return ''
    }
}

const serializeText = (node: CustomText): string => {
    let string = escapeHtml(node.text)
    if (node.bold) {
        string = `<strong>${string}</strong>`
    }
    if (node.italic) {
        string = `<em>${string}</em>`
    }
    if (node.underline) {
        string = `<u>${string}</u>`
    }
    return string
}

const serializeElement = (node: CustomElement): string => {
    const children = serialize(node.children)
    switch (node.type) {
        case 'h1':
        case 'h2':
        case 'h3':
            return `<${node.type}>${children}</${node.type}>`
        case 'paragraph':
            return `<p>${children}</p>`
        default:
            return children
    }
}
